"use strict";

import angular from "angular";
import _ from "lodash";
import { SID_LOOKUP } from "../../app.constants";
import { saveAs } from "file-saver";
import { zip } from "fflate";
import { Notyf } from "notyf";
import OneSignal from "react-onesignal";
import { PDFDocument } from "pdf-lib";
import { underscoreSanitize } from "./common.utils";
import { MAX_CLOUDINARY_FILE_SIZE } from "./common.constants";

export class OneSignalService {
    /* @ngInject */
    constructor($translate, $q) {
        this.$translate = $translate;
        this.$q = $q;
        this.initializedPromise = null;
    }

    init() {
        let svc = this;
        if (svc.initializedPromise) {
            return svc.initializedPromise;
        }

        let appId = process.env.ONESIGNAL_APP_ID;
        if (!appId) {
            return svc.$q.when(false);
        }

        svc.initializedPromise = svc.$q
            .when(
                OneSignal.init({
                    appId: appId,
                }),
            )
            .then(function () {
                return true;
            })
            .catch(function (error) {
                console.warn("OneSignal init failed", error);
                return false;
            });

        return svc.initializedPromise;
    }

    login(userSid) {
        let svc = this;
        if (!userSid) {
            return svc.$q.when();
        }

        return svc.init().then(function (initialized) {
            if (!initialized) {
                return;
            }
            let language = svc.$translate.use();
            return svc.$q.when(OneSignal.login(userSid)).then(
                function () {
                    if (!language) {
                        return;
                    }
                    return svc.$q.when(OneSignal.User.setLanguage(language));
                },
                function (error) {
                    // Don't reject the promise on login failure since it could be due to ad blockers and we don't want that to interfere with the rest of the app
                    console.error("OneSignal login failed", error);
                },
            );
        });
    }

    logout() {
        let svc = this;
        return svc.init().then(function (initialized) {
            if (!initialized) {
                return;
            }
            return svc.$q.when(OneSignal.logout());
        });
    }

    promptPush() {
        let svc = this;
        return svc.init().then(function (initialized) {
            if (!initialized) {
                return;
            }
            return svc.$q.when(OneSignal.Slidedown.promptPush());
        });
    }
}

export class EngagementModalService {
    /* @ngInject */
    constructor($compile, $rootScope, $document) {
        this.$compile = $compile;
        this.$rootScope = $rootScope;
        this.$document = $document;
    }

    open(type = "review") {
        let svc = this;
        if (svc.activeScope) {
            return;
        }

        let scope = svc.$rootScope.$new(true);
        let element = angular.element(
            `<engagement-modal popup-type="${type}"></engagement-modal>`,
        );

        svc.$document.find("body").eq(0).append(element);
        svc.$compile(element)(scope);

        svc.activeScope = scope;
        svc.activeElement = element;

        scope.$on("$destroy", function () {
            svc.activeScope = undefined;
            if (svc.activeElement) {
                svc.activeElement.remove();
                svc.activeElement = undefined;
            }
        });
    }
}

export class CanvasService {
    /* @ngInject */
    constructor($q) {
        this.$q = $q;
    }

    getBlob(canvas, quality = 0.9) {
        let svc = this;
        let deferred = svc.$q.defer();
        try {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        deferred.reject(
                            new Error("Canvas toBlob returned null"),
                        );
                        return;
                    }
                    deferred.resolve(blob);
                },
                "image/jpeg",
                quality,
            );
        } catch (error) {
            deferred.reject(error);
        }
        return deferred.promise;
    }
}

export class ImageShrinkService {
    /* @ngInject */
    constructor($q, $window, $document, CanvasService) {
        this.$q = $q;
        this.$window = $window;
        this.$document = $document;
        this.CanvasService = CanvasService;
        this.SCALE_STEP = 0.85;
    }

    shrink(file, targetBytes, options = {}) {
        let svc = this;
        let jpegQuality = _.get(options, "jpegQuality", 0.9);
        let maxDimension = _.get(options, "maxDimension");
        let hasMaxDimension = _.isFinite(maxDimension) && maxDimension > 0;

        return svc.$q
            .when(svc.$window.createImageBitmap(file))
            .then(function (imageBitmap) {
                let canvas = svc.$document[0].createElement("canvas");
                let context = canvas.getContext("2d", { alpha: false });
                let initialWidth = imageBitmap.width;
                let initialHeight = imageBitmap.height;

                if (hasMaxDimension) {
                    let largestSide = Math.max(initialWidth, initialHeight);
                    if (largestSide > maxDimension) {
                        let resizeRatio = maxDimension / largestSide;
                        initialWidth = Math.max(
                            1,
                            Math.round(initialWidth * resizeRatio),
                        );
                        initialHeight = Math.max(
                            1,
                            Math.round(initialHeight * resizeRatio),
                        );
                    }
                }

                function attemptEncode(
                    imageBitmap,
                    canvas,
                    context,
                    width,
                    height,
                ) {
                    canvas.width = width;
                    canvas.height = height;
                    context.drawImage(imageBitmap, 0, 0, width, height);

                    return svc.CanvasService.getBlob(canvas, jpegQuality).then(
                        function (blob) {
                            if (
                                blob.size <= targetBytes ||
                                (width === 1 && height === 1)
                            ) {
                                return blob;
                            }

                            let nextWidth = Math.max(
                                1,
                                Math.round(width * svc.SCALE_STEP),
                            );
                            let nextHeight = Math.max(
                                1,
                                Math.round(height * svc.SCALE_STEP),
                            );
                            return attemptEncode(
                                imageBitmap,
                                canvas,
                                context,
                                nextWidth,
                                nextHeight,
                            );
                        },
                    );
                }

                return attemptEncode(
                    imageBitmap,
                    canvas,
                    context,
                    initialWidth,
                    initialHeight,
                ).finally(function () {
                    if (imageBitmap.close) {
                        imageBitmap.close();
                    }
                });
            });
    }
}

export class ThrottledMapService {
    /* @ngInject */
    constructor($q) {
        this.$q = $q;
    }

    wrap(collection, asyncOperation, maxConcurrency = 10) {
        let svc = this;
        let deferreds = _.map(collection, function () {
            return svc.$q.defer();
        });
        let nextIndexToStart = 0;
        let activeCount = 0;

        function scheduleMore() {
            while (
                activeCount < maxConcurrency &&
                nextIndexToStart < collection.length
            ) {
                let index = nextIndexToStart;
                nextIndexToStart += 1;
                activeCount += 1;
                let deferred = deferreds[index];

                let operationPromise;
                try {
                    operationPromise = asyncOperation(collection[index], index);
                } catch (error) {
                    deferred.reject(error);
                    activeCount -= 1;
                    continue;
                }

                svc.$q
                    .when(operationPromise)
                    .then(deferred.resolve, deferred.reject)
                    .finally(function () {
                        activeCount -= 1;
                        scheduleMore();
                    });
            }
        }

        scheduleMore();
        return _.map(deferreds, function (deferred) {
            return deferred.promise;
        });
    }
}

export class ToastService {
    /* @ngInject */
    constructor($translate) {
        this.$translate = $translate;
        const TOAST_DURATION = 4000;
        this.notyf = new Notyf({
            duration: TOAST_DURATION,
            position: {
                x: "left",
                y: "bottom",
            },
            types: [
                {
                    type: "success",
                    icon: false,
                    background: "#323232",
                    className: "text-base",
                },
                {
                    type: "error",
                    icon: false,
                    className: "text-base",
                },
            ],
        });
        let ctrl = this;
        ctrl.create = _.throttle(function (
            translationKey,
            values = {},
            options = {},
        ) {
            if (!_.isEmpty(translationKey)) {
                return this.$translate(translationKey, values).then(
                    function (message) {
                        options.message = message;
                        return ctrl.notyf.success(options);
                    },
                );
            }
        }, TOAST_DURATION);
    }
}

export class DownloadService {
    /* @ngInject */
    constructor($q, $http) {
        this.$q = $q;
        this.$http = $http;
    }

    getBlob(url) {
        let svc = this;
        return svc.$http.get(url, {
            responseType: "blob",
        });
    }

    get(url, additionalProperties) {
        let svc = this;
        return svc.$http
            .get(url, { responseType: "arraybuffer" })
            .then((resp) => _.merge({ data: resp.data }, additionalProperties));
    }

    baseZip(formatted) {
        let svc = this;
        let deferred = svc.$q.defer();
        zip(formatted, { level: 0 }, (err, dat) =>
            err ? deferred.reject(err) : deferred.resolve(dat),
        );
        return deferred.promise;
    }

    zip(files) {
        let svc = this;
        let newZipFormattedData = {};
        _.forEach(files, function (file) {
            let filename = underscoreSanitize(file.name) + "." + file.extension;
            newZipFormattedData[filename] = new Uint8Array(file.data);
        });
        return svc.baseZip(newZipFormattedData);
    }

    save(zipped, fileName) {
        saveAs(new Blob([zipped.buffer]), fileName + ".zip");
    }

    fetchAndSave(fileSpecs, fileName) {
        let svc = this;
        let fileResponse = _.map(fileSpecs, function (file) {
            return svc.get(file.url, file);
        });
        return svc.$q.all(fileResponse).then(function (documents) {
            return svc.zip(documents).then(function (zipped) {
                svc.save(zipped, fileName);
            });
        });
    }
}

export class PrintService {
    /* @ngInject */
    constructor($q, $window) {
        this.$q = $q;
        this.$window = $window;
    }

    printFromUrl(url) {
        let svc = this;
        return svc.getBlobFromUrl(url).then(function (blob) {
            return svc.printBlob(blob);
        });
    }

    getBlobFromUrl(url) {
        let svc = this;
        return svc.$q.when(svc.$window.fetch(url)).then(function (response) {
            if (!response.ok) {
                let deferred = svc.$q.defer();
                deferred.reject(response);
                return deferred.promise;
            }
            return svc.$q.when(response.blob());
        });
    }

    printPdfsFromUrls(urls) {
        let svc = this;
        let pdfBlobRequests = [];
        for (let url of urls) {
            pdfBlobRequests.push(svc.getBlobFromUrl(url));
        }
        return svc.$q
            .all(pdfBlobRequests)
            .then(function (response) {
                return svc.mergePdfBlobs(response);
            })
            .then(function (blob) {
                return svc.printBlob(blob);
            });
    }

    mergePdfBlobs(pdfBlobs) {
        let svc = this;
        return svc.$q.when(PDFDocument.create()).then(function (mergedPdf) {
            let mergePromise = svc.$q.when();
            for (let pdfBlob of pdfBlobs) {
                mergePromise = mergePromise
                    .then(function () {
                        return svc.$q.when(pdfBlob.arrayBuffer());
                    })
                    .then(function (pdfBytes) {
                        return svc.$q.when(PDFDocument.load(pdfBytes));
                    })
                    .then(function (sourcePdf) {
                        return svc.$q.when(
                            mergedPdf.copyPages(
                                sourcePdf,
                                sourcePdf.getPageIndices(),
                            ),
                        );
                    })
                    .then(function (copiedPages) {
                        for (let copiedPage of copiedPages) {
                            mergedPdf.addPage(copiedPage);
                        }
                    });
            }
            return mergePromise
                .then(function () {
                    return svc.$q.when(mergedPdf.save());
                })
                .then(function (pdfBytes) {
                    return new svc.$window.Blob([pdfBytes], {
                        type: "application/pdf",
                    });
                });
        });
    }

    printBlob(blob) {
        let svc = this;
        let deferred = svc.$q.defer();
        let doc = svc.$window.document;
        let objectUrl = svc.$window.URL.createObjectURL(blob);
        let iframe = doc.createElement("iframe");
        let hasCleanedUp = false;
        let hasSettled = false;

        function cleanup() {
            if (hasCleanedUp) {
                return;
            }
            hasCleanedUp = true;
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
            svc.$window.URL.revokeObjectURL(objectUrl);
        }

        function resolveOnce(value) {
            if (hasSettled) {
                return;
            }
            hasSettled = true;
            deferred.resolve(value);
        }

        function rejectOnce(reason) {
            if (hasSettled) {
                return;
            }
            hasSettled = true;
            deferred.reject(reason);
        }

        iframe.style.display = "none";
        iframe.src = objectUrl;

        iframe.onload = function () {
            try {
                iframe.contentWindow.onafterprint = function () {
                    cleanup();
                };
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                resolveOnce();
                svc.$window.setTimeout(function () {
                    cleanup();
                }, 60000);
            } catch (reason) {
                cleanup();
                rejectOnce(reason);
            }
        };

        iframe.onerror = function (reason) {
            cleanup();
            rejectOnce(reason);
        };

        (doc.body || doc.documentElement).appendChild(iframe);

        return deferred.promise;
    }
}

export class VersionCheckerService {
    /* @ngInject */
    constructor($http, $interval) {
        this.$http = $http;
        this.$interval = $interval;
        this.DEFAULT_VERSION_POLL_INTERVAL_MS = 15 * 60 * 1000;
    }

    start(options = {}) {
        let svc = this;
        let currentVersion = process.env.APP_VERSION;
        let versionUrl = options.versionUrl || "/version.json";
        let pollIntervalMs =
            options.pollIntervalMs || svc.DEFAULT_VERSION_POLL_INTERVAL_MS;
        let onUpdateAvailable = options.onUpdateAvailable;

        console.log("[VersionCheckerService] start", {
            currentVersion: currentVersion,
            versionUrl: versionUrl,
            pollIntervalMs: pollIntervalMs,
        });

        if (!_.isFunction(onUpdateAvailable)) {
            throw new Error(
                "VersionCheckerService.start requires onUpdateAvailable callback",
            );
        }

        let hasTriggeredUpdate = false;
        let intervalPromise = null;

        function triggerUpdate() {
            if (hasTriggeredUpdate) {
                console.log(
                    "[VersionCheckerService] triggerUpdate skipped: already triggered",
                );
                return;
            }

            hasTriggeredUpdate = true;
            console.log("[VersionCheckerService] triggerUpdate fired", {
                currentVersion: currentVersion,
            });
            if (intervalPromise) {
                svc.$interval.cancel(intervalPromise);
            }
            onUpdateAvailable();
        }

        function checkForUpdate() {
            if (hasTriggeredUpdate) {
                console.log(
                    "[VersionCheckerService] checkForUpdate skipped: already triggered",
                );
                return;
            }

            console.log("[VersionCheckerService] checkForUpdate tick", {
                currentVersion: currentVersion,
                versionUrl: versionUrl,
            });

            svc.$http
                .get(versionUrl, {
                    cache: false,
                    headers: {
                        "Cache-Control": "no-store",
                    },
                })
                .then(function (response) {
                    let payload = response.data;
                    if (!payload || !payload.version) {
                        console.log(
                            "[VersionCheckerService] checkForUpdate missing payload.version",
                            payload,
                        );
                        return;
                    }

                    console.log(
                        "[VersionCheckerService] checkForUpdate versions",
                        {
                            currentVersion: currentVersion,
                            payloadVersion: payload.version,
                        },
                    );

                    if (payload.version !== currentVersion) {
                        triggerUpdate();
                    }
                })
                .catch(function (error) {
                    console.error(
                        "[VersionCheckerService] checkForUpdate request failed",
                        error,
                    );
                });
        }
        checkForUpdate();
        intervalPromise = svc.$interval(checkForUpdate, pollIntervalMs);

        return function stopVersionChecker() {
            if (intervalPromise) {
                console.log("[VersionCheckerService] stopping version checker");
                svc.$interval.cancel(intervalPromise);
            }
        };
    }
}

export class CloudinaryUploadService {
    /* @ngInject */
    constructor($q, $window, $document) {
        this.$q = $q;
        this.$window = $window;
        this.$document = $document;
        this.cloudinaryLoadPromise = null;
        this.CLOUDINARY_LIB_URL =
            "https://upload-widget.cloudinary.com/global/all.js";
    }

    loadCloudinary() {
        let svc = this;
        if (svc.$window.cloudinary) {
            return svc.$q.when(svc.$window.cloudinary);
        }
        if (svc.cloudinaryLoadPromise) {
            return svc.cloudinaryLoadPromise;
        }

        let deferred = svc.$q.defer();
        let documentElement = svc.$document[0];
        let script = _.find(
            documentElement.getElementsByTagName("script"),
            function (existingScript) {
                return existingScript.src === svc.CLOUDINARY_LIB_URL;
            },
        );

        function resolveCloudinaryIfPresent() {
            if (svc.$window.cloudinary) {
                deferred.resolve(svc.$window.cloudinary);
                return true;
            }
            return false;
        }

        if (resolveCloudinaryIfPresent()) {
            svc.cloudinaryLoadPromise = deferred.promise;
            return svc.cloudinaryLoadPromise;
        }

        function onLoad() {
            cleanupListeners();
            if (!resolveCloudinaryIfPresent()) {
                deferred.reject(
                    new Error("Cloudinary library did not initialize"),
                );
            }
        }

        function onError(error) {
            cleanupListeners();
            if (script && script.parentNode) {
                script.parentNode.removeChild(script);
            }
            deferred.reject(error);
        }

        function cleanupListeners() {
            if (!script) {
                return;
            }
            script.removeEventListener("load", onLoad);
            script.removeEventListener("error", onError);
        }

        if (!script) {
            script = documentElement.createElement("script");
            script.src = svc.CLOUDINARY_LIB_URL;
            script.async = true;
            (documentElement.head || documentElement.body).appendChild(script);
        }

        script.addEventListener("load", onLoad);
        script.addEventListener("error", onError);

        svc.cloudinaryLoadPromise = deferred.promise.then(
            function (cloudinary) {
                return cloudinary;
            },
            function (error) {
                svc.cloudinaryLoadPromise = null;
                return svc.$q.reject(error);
            },
        );

        return svc.cloudinaryLoadPromise;
    }

    openUpload(config) {
        let svc = this;
        return svc.loadCloudinary().then(function (cloudinary) {
            let deferred = svc.$q.defer();
            let widget = cloudinary.createUploadWidget(
                config,
                (error, result) => {
                    if (error) {
                        deferred.reject(error);
                        widget.close();
                        return;
                    }
                    if (result && result.event === "success") {
                        deferred.resolve(result);
                        widget.close();
                    }
                },
            );
            widget.open();
            return deferred.promise;
        });
    }
}

export class ResourceService {
    /* @ngInject */
    constructor(Restangular, $q, resource, slug_field = SID_LOOKUP) {
        this.Restangular = Restangular;
        this.$q = $q;
        this.resource = resource;
        this.slug_field = slug_field;
        this.SEVERAL_MAX = 50;
    }

    createInstance(sid) {
        let svc = this;
        return svc.Restangular.one(svc.resource, sid);
    }

    create(data) {
        return this.Restangular.all(this.resource).post(data);
    }

    retrieve(sid, params = {}, options = {}) {
        let expand = _.get(options, "expand", []);
        if (!_.isEmpty(expand)) {
            params.expand = _.join(expand);
        }
        if (sid) {
            return this.Restangular.one(this.resource, sid).get(params);
        }
    }

    list(params = {}, options = {}) {
        let svc = this;
        let listParams = _.assign({}, params);
        let orderBy = _.get(options, "orderBy", undefined);
        let ascending = _.get(options, "ascending", true);
        let expand = _.get(options, "expand", []);
        let emptyIn = false;

        function getInLookupValues(value) {
            if (_.isArray(value)) {
                return _.compact(
                    _.map(value, function (lookupValue) {
                        return _.trim(_.toString(lookupValue));
                    }),
                );
            }
            if (_.isString(value)) {
                return _.compact(
                    _.map(_.split(value, ","), function (lookupValue) {
                        return _.trim(lookupValue);
                    }),
                );
            }
            if (_.isNil(value)) {
                return [];
            }
            return [_.trim(_.toString(value))];
        }

        let oversizedInLookup = null;
        _.forEach(listParams, function (value, key) {
            if (!_.endsWith(key, "__in")) {
                return;
            }

            let inLookupValues = getInLookupValues(value);
            if (!inLookupValues.length) {
                emptyIn = true;
                return;
            }

            listParams[key] = _.join(inLookupValues);

            if (!oversizedInLookup && inLookupValues.length > svc.SEVERAL_MAX) {
                oversizedInLookup = {
                    key,
                    values: _.uniq(inLookupValues),
                };
            }
        });
        if (orderBy) {
            let orderByParam = orderBy;
            if (!ascending) {
                orderByParam = "-" + orderByParam;
            }
            listParams.order_by = orderByParam;
        }
        if (!_.isEmpty(expand)) {
            listParams.expand = _.join(expand);
        }

        if (emptyIn) {
            let deferred = svc.$q.defer();
            deferred.resolve([]);
            return deferred.promise;
        }

        if (oversizedInLookup) {
            let chunks = _.chunk(oversizedInLookup.values, svc.SEVERAL_MAX);
            let responses = _.map(chunks, function (chunkValues) {
                let chunkParams = _.assign({}, listParams);
                chunkParams[oversizedInLookup.key] = _.join(chunkValues);
                return svc.list(chunkParams, options);
            });
            return svc.$q
                .all(responses)
                .then(_.flatten)
                .then(function (results) {
                    if (orderBy) {
                        results = _.orderBy(
                            results,
                            orderBy,
                            ascending ? "asc" : "desc",
                        );
                    }
                    return svc.Restangular.restangularizeCollection(
                        undefined,
                        results,
                        svc.resource,
                        true,
                    );
                });
        }

        return svc.Restangular.all(svc.resource).getList(listParams);
    }

    several(many) {
        return this.severalByLookup(many, this.slug_field + "__in", {});
    }

    severalByLookup(many, lookupKey, baseParams = {}) {
        let svc = this;
        let lookupValues = _.uniq(many);

        if (!lookupValues.length) {
            return svc.$q.when(
                svc.Restangular.restangularizeCollection(
                    undefined,
                    [],
                    svc.resource,
                    true,
                ),
            );
        }

        let params = _.assign({}, baseParams);
        params[lookupKey] = lookupValues;
        return svc.list(params);
    }

    head(params = {}, orderBy, limit, ascending = true) {
        return this.list(params, {
            orderBy: orderBy,
            ascending: ascending,
        }).then((results) => _.take(results, limit));
    }

    first(params = {}, orderBy, ascending = true) {
        return this.list(params, {
            orderBy: orderBy,
            ascending: ascending,
        }).then(_.first);
    }
}

export const cloudinaryUploadWidgetStyles = {
    palette: {
        window: "#FFF",
        windowBorder: "rgba(94, 65, 15, 0.7)",
        tabIcon: "#CC8B1D",
        menuIcons: "#5E410F",
        textDark: "#000000",
        textLight: "#FFFFFF",
        link: "rgba(94, 65, 15, 0.7)",
        action: "#CC8B1D",
        inactiveTabIcon: "rgba(94, 65, 15, 0.7)",
        error: "#F44235",
        inProgress: "#0078FF",
        complete: "#8FC73B",
        sourceBg: "#E4EBF1",
    },
};

export const cloudinaryProfilePictureConfig = {
    cloudName: "harvust",
    uploadPreset: "profile_picture",
    sources: ["camera", "local"],
    defaultSource: "camera",
    styles: cloudinaryUploadWidgetStyles,
    showPoweredBy: false,
    maxFileSize: MAX_CLOUDINARY_FILE_SIZE,
};

export const cloudinaryLogoPictureConfig = {
    cloudName: "harvust",
    uploadPreset: "business_detail_logo",
    sources: ["local"],
    defaultSource: "local",
    styles: cloudinaryUploadWidgetStyles,
    showPoweredBy: false,
    maxFileSize: MAX_CLOUDINARY_FILE_SIZE,
};

export const cloudinaryAttachmentsConfig = {
    cloudName: "harvust",
    uploadPreset: "attachments",
    sources: ["local"],
    defaultSource: "local",
    styles: cloudinaryUploadWidgetStyles,
    showPoweredBy: false,
    maxFileSize: MAX_CLOUDINARY_FILE_SIZE,
};
